import type { CataloguePlan } from './api'
import { disputeLockLabel } from './format'

/** Buyer-facing one-liner from docs/plans/06-subscription-plans-and-personas.md */
export function planTagline(plan: string): string {
  switch (plan) {
    case 'solo':
      return 'One sender.'
    case 'team':
      return 'Several senders.'
    case 'business':
      return 'Higher volume, with seven-year retention.'
    default:
      return ''
  }
}

export type PlanFeature = {
  label: string
  included: boolean
}

/** Volume and gates that differ between tiers; workflow features are on every paid plan. */
export function planFeatures(plan: CataloguePlan): PlanFeature[] {
  const lim = plan.limits
  const f = plan.features
  if (!lim) return []

  const seats = lim.max_sender_seats
  const retentionYears = Math.round((lim.max_retention_days ?? 365) / 365)

  return [
    { label: `${seats} sender seat${seats === 1 ? '' : 's'}`, included: true },
    { label: `${lim.max_active_envelopes} active envelopes`, included: true },
    { label: `${lim.max_completions_per_month} completions / month`, included: true },
    { label: `${retentionYears}-year retention`, included: true },
    { label: 'Unlimited view-only members', included: Boolean(f.free_view_only_seats) },
    { label: 'Shared org templates', included: plan.plan !== 'solo' },
    {
      // The server emits `sms_included_per_month`. This read `sms_included`,
      // which is always undefined — so Business, the ONE tier where SMS is
      // included, came out as `sms_add_on: false` and rendered "SMS add-on
      // available" struck through, while the cheaper tiers advertised it.
      // Nothing could catch it: `features` is a Record<string, boolean|number>
      // so tsc is blind, and catalogue_test.go asserts struct fields rather
      // than JSON tags.
      label:
        typeof f.sms_included_per_month === 'number' && f.sms_included_per_month > 0
          ? `${f.sms_included_per_month} SMS / month included`
          : 'SMS add-on available',
      included: Boolean(f.sms_included_per_month) || Boolean(f.sms_add_on),
    },
  ]
}

/** Optional add-ons — not listed on every plan card; see Advanced on the marketing calculator. */
export function planAdvancedAddOns(plan: CataloguePlan): PlanFeature[] {
  const f = plan.features
  return [{ label: disputeLockLabel, included: Boolean(f.legal_hold) }]
}

/** On every paid tier — shown once below the grid. */
export const plansIncludeAlways = [
  'Offline-verifiable signatures on every envelope',
  'Reminders, conditional routing, signing groups, and custom branding',
  'Full send, sign, and template workflow',
] as const

export function annualSavingsPercent(monthlyCents: number, annualCents: number): number | null {
  if (!monthlyCents || !annualCents) return null
  const fullYear = monthlyCents * 12
  if (fullYear <= annualCents) return null
  return Math.round(((fullYear - annualCents) / fullYear) * 100)
}
